// @ts-nocheck
import {type ColorRepresentation, Vector3} from 'three';
import {LineDashedMaterial, NodeMaterial, type NodeMaterialParameters} from 'three/webgpu';
import {
    attribute,
    cameraProjectionMatrix,
    cameraViewMatrix,
    dashSize,
    float,
    gapSize,
    int,
    Loop,
    materialLineDashOffset,
    materialLineDashSize,
    materialLineGapSize,
    modelWorldMatrix,
    uint,
    uniform,
    uniformArray,
    vec2,
    vec4,
    viewportSize,
} from 'three/tsl';

const DEFAULT_VALUES = new LineDashedMaterial();

export const LINE_DISTANCE_PERCENT_ATTRIBUTE_NAME = 'lineDistancePercent';

export type ILinePointValue = Vector3 | readonly [number, number, number];

export interface IDashDotLIneNodeMaterialOption extends NodeMaterialParameters {
    color?: ColorRepresentation;
    dashSize?: number;
    gapSize?: number;
    dotSize?: number;
    linewidth?: number;
    opacity?: number;
    scale?: number;
    transparent?: boolean;
    points?: ILinePointValue[];
}


function normalizeLinePoint(point: ILinePointValue): Vector3 {
    if (point instanceof Vector3) {
        return point.clone();
    }

    return new Vector3(point[0], point[1], point[2]);
}

export class ThinChainLIneNodeMaterial extends NodeMaterial {
    public readonly isDashDotLIneNodeMaterial: boolean = true;

    public dashSize: number = 22;

    public gapSize: number = 4;

    public scale: number = 1;

    private _dotSize: number = 4;

    private _points: Vector3[] = [];

    private pointsUniformNode: ReturnType<typeof uniformArray> | null = null;

    private readonly pointsLengthUniform = uniform(uint(0));

    public readonly dotSizeUniform = uniform(4);

    public set dotSize(value: number) {
        this._dotSize = value;
        this.dotSizeUniform.value = value;
    }

    public get dotSize(): number {
        return this._dotSize;
    }


    public set points(value: ILinePointValue[]) {
        const normalizedPoints = value.map((point) => normalizeLinePoint(point));
        const hasValidPolyline = normalizedPoints.length >= 2;
        const nextPoints = hasValidPolyline ? normalizedPoints : [];

        this.pointsLengthUniform.value = nextPoints.length;

        if (this.pointsUniformNode && this._points.length === nextPoints.length) {
            this._points = nextPoints;
            this.pointsUniformNode.array = this._points;
            return;
        }

        this._points = nextPoints;

        if (hasValidPolyline) {
            this.pointsUniformNode = uniformArray(this._points, 'vec3');
            this.needsUpdate = true;
            return;
        }

        const hadPointsUniformNode = this.pointsUniformNode !== null;
        this.pointsUniformNode = null;

        if (hadPointsUniformNode) {
            this.needsUpdate = true;
        }
    }


    public get points(): Vector3[] {
        return this._points;
    }


    public constructor(parameters: IDashDotLIneNodeMaterialOption = {}) {
        super();

        this.setDefaultValues(DEFAULT_VALUES);
        this.dashSize = parameters.dashSize ?? this.dashSize;
        this.gapSize = parameters.gapSize ?? this.gapSize;
        this.dotSize = parameters.dotSize ?? this.dotSize;
        this.scale = parameters.scale ?? this.scale;

        if (parameters.points) {
            this.points = parameters.points;
        }

        const {scale: _scale, points: _points, ...materialParameters} = parameters;
        void _scale;
        void _points;

        this.setValues(materialParameters);
    }

    public setupVariants(): void {
        if (!this.pointsUniformNode) {
            return;
        }

        const lineDistancePercentNode = attribute(
            LINE_DISTANCE_PERCENT_ATTRIBUTE_NAME,
        )

        const worldToScreen = (worldPositionNode) => {
            const clipNode = cameraProjectionMatrix.mul(
                cameraViewMatrix.mul(vec4(worldPositionNode, 1.0)),
            ) ;
            const ndcNode = clipNode.xy.div(clipNode.w);

            return vec2(
                ndcNode.x.mul(0.5).add(0.5).mul(viewportSize.x),
                ndcNode.y.mul(-0.5).add(0.5).mul(viewportSize.y),
            )
        };

        const activePointsUniformNode = this.pointsUniformNode;
        const pointsLengthNode = int(this.pointsLengthUniform);
        const totalScreenDistanceNode = float(0).toVar('totalScreenDistance');
        const segmentCountNode = pointsLengthNode.sub(int(1)).toVar('segmentCount');

        Loop(segmentCountNode, ({i}) => {
            const segmentStartNode = activePointsUniformNode.element(i)
            const segmentEndNode = activePointsUniformNode.element(i.add(int(1)))
            const segmentStartWorldNode = (
                modelWorldMatrix.mul(vec4(segmentStartNode, 1.0))
            ).xyz
            const segmentEndWorldNode = (
                modelWorldMatrix.mul(vec4(segmentEndNode, 1.0))
            ).xyz
            const segmentStartScreenNode = worldToScreen(segmentStartWorldNode);
            const segmentEndScreenNode = worldToScreen(segmentEndWorldNode);
            const segmentScreenDistanceNode = float(
                segmentStartScreenNode.distance(segmentEndScreenNode),
            ).toVar('segmentScreenDistance');

            totalScreenDistanceNode.addAssign(segmentScreenDistanceNode);
        });

        const lineDistanceNode = totalScreenDistanceNode.mul(lineDistancePercentNode)
        const dashSizeNode = materialLineDashSize
        const gapSizeNode = materialLineGapSize
        const offsetNode = materialLineDashOffset
        const dotSizeNode = this.dotSizeUniform

        dashSize.assign(dashSizeNode);
        gapSize.assign(gapSizeNode);

        const lineDistanceWithOffsetNode = lineDistanceNode.add(offsetNode)
        const cycleSizeNode = dashSize.add(gapSize).add(dotSizeNode).add(gapSize);
        const positionNode = float(lineDistanceWithOffsetNode.mod(cycleSizeNode)).toVar('cyclePosition');
        const dashEndNode = dashSize;
        const firstGapEndNode = dashSize.add(gapSize);
        const dotEndNode = firstGapEndNode.add(dotSizeNode);

        positionNode
            .greaterThan(dashEndNode)
            .and(positionNode.lessThan(firstGapEndNode))
            .discard();

        positionNode
            .greaterThan(dotEndNode)
            .discard();
    }
}